import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { Heart, Flame, Music2, BookOpen, Sparkles, LogOut, ArrowRight, ShoppingBag, Download, Gift, Library, BadgeCheck, Repeat2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { RELEASES, ACTIVATIONS, WISDOM, BOOKS } from "@/lib/content";
import { listMyOrders, getMyDownload, listMyDownloads, getAccountAutomation, createBillingPortalSession } from "@/lib/shop.functions";
import { PushOptInButton } from "@/components/site/PushOptInButton";
import { UserBadges } from "@/components/site/UserBadges";
import { getStripeEnvironment } from "@/lib/stripe";

export const Route = createFileRoute("/account")({
  head: () => ({
    meta: [
      { title: "Account · Buddha KA" },
      { name: "description", content: "Your saved transmissions, activation streak, and listening history." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AccountPage,
});

type Kind = "music" | "song" | "wisdom" | "activation" | "book";
/** Kinds this page can resolve, plus the BKLS beat kinds it only counts. */
type FavKind = Kind | "beat" | "beat_wishlist";
interface FavRow { id: string; kind: FavKind; slug: string; created_at: string }
interface CompletionRow { id: string; activation_slug: string; completed_at: string }
interface ListenRow { id: string; release_slug: string; song_slug: string | null; played_at: string }
interface OrderRow { id: string; order_number?: string | null; product_slug: string; product_title: string; amount_cents: number; currency: string; status: string; environment: string; fulfillment_status: string; tracking_number: string | null; tracking_url: string | null; shipping_address: unknown; refunded_at: string | null; reward_points_awarded?: number; created_at: string }
interface AutomationData {
  rewards: { balance: number; recent: Array<{ id: string; points: number; reason: string; description: string | null; created_at: string }> };
  referral: { code: string; status: string } | null;
  library: Array<{ id: string; product_slug: string; granted_at: string; product: ShopProduct | null }>;
  subscription: { membership_tier: string | null; price_id?: string | null; status: string; current_period_end: string | null; cancel_at_period_end: boolean; environment: string } | null;
  recommendations: Array<{ id: string; product_slug: string; reason: string; product: ShopProduct | null }>;
  reviewRequests: Array<{ id: string; product_slug: string; due_at: string; status: string; product: ShopProduct | null }>;
}
interface ShopProduct { slug: string; title: string; subtitle: string | null; image_url: string | null; amount_cents: number; currency: string; kind: string }

function AccountPage() {
  const navigate = useNavigate();
  const [email, setEmail] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [favorites, setFavorites] = useState<FavRow[]>([]);
  const [completions, setCompletions] = useState<CompletionRow[]>([]);
  const [listening, setListening] = useState<ListenRow[]>([]);
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [automation, setAutomation] = useState<AutomationData | null>(null);
  const [origin, setOrigin] = useState("");

  useEffect(() => {
    let cancelled = false;
    async function load() {
      const { data: userData } = await supabase.auth.getUser();
      if (cancelled) return;
      setOrigin(window.location.origin);
      if (!userData.user) {
        navigate({ to: "/auth" });
        return;
      }
      setEmail(userData.user.email ?? null);
      const [f, c, l, o, a] = await Promise.all([
        supabase.from("user_favorites").select("id, kind, slug, created_at").order("created_at", { ascending: false }),
        supabase.from("activation_completions").select("id, activation_slug, completed_at").order("completed_at", { ascending: false }).limit(500),
        supabase.from("listening_history").select("id, release_slug, song_slug, played_at").order("played_at", { ascending: false }).limit(20),
        listMyOrders().catch(() => [] as OrderRow[]),
        getAccountAutomation({ data: { environment: getStripeEnvironment() } }).catch(() => null),
      ]);
      if (cancelled) return;
      setFavorites((f.data as FavRow[]) ?? []);
      setCompletions((c.data as CompletionRow[]) ?? []);
      setListening((l.data as ListenRow[]) ?? []);
      setOrders((o as OrderRow[]) ?? []);
      setAutomation(a as AutomationData | null);
      setLoading(false);
    }
    void load();
  }, [navigate]);

  const streak = useMemo(() => calculateStreak(completions.map((r) => r.completed_at)), [completions]);

  // Resolve saves up front so the heading count matches what actually
  // renders. Previously every unresolvable row (notably beat saves, which
  // this page has no resolver for) was dropped inside the row component
  // while still being counted, so "Saved (5)" could show only 3 cards.
  const savedItems = useMemo(
    () =>
      favorites
        .map((row) => ({ row, info: resolveItem(row.kind, row.slug) }))
        .filter((x): x is { row: FavRow; info: { title: string; href: string } } => x.info !== null),
    [favorites],
  );
  // Beat saves live on the BKLS account page, which resolves them against
  // the public catalog and marks pulled beats as unavailable. We only
  // surface the count here and link across, so this page never links to a
  // beat page whose publication state it hasn't checked.
  const beatSaveCount = useMemo(
    () => favorites.filter((f) => f.kind === "beat" || f.kind === "beat_wishlist").length,
    [favorites],
  );

  const totalCompletions = completions.length;

  const signOut = async () => {
    await supabase.auth.signOut();
    navigate({ to: "/" });
  };

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Loading the field…</p>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-24">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">Your field</p>
          <h1 className="mt-3 font-serif text-4xl sm:text-5xl">Account</h1>
          {email && <p className="mt-2 text-sm text-muted-foreground">{email}</p>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <PushOptInButton />
          <Link to="/ascension" className="inline-flex items-center gap-2 rounded-full border border-gold px-4 py-2 text-xs text-gold-luminous hover:bg-gold hover:text-obsidian">
            <Sparkles size={12} aria-hidden /> Ascension Dashboard
          </Link>
          <Link to="/playlists" className="inline-flex items-center gap-2 rounded-full border hairline px-4 py-2 text-xs text-muted-foreground hover:border-gold hover:text-gold">
            <Library size={12} aria-hidden /> Playlists
          </Link>
          <button
            type="button"
            onClick={signOut}
            className="inline-flex items-center gap-2 rounded-full border hairline px-4 py-2 text-xs text-muted-foreground hover:border-gold hover:text-gold"
          >
            <LogOut size={12} aria-hidden /> Sign out
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="mt-10 grid gap-4 sm:grid-cols-3">
        <StatCard eyebrow="Current Streak" value={streak.current} unit={streak.current === 1 ? "day" : "days"} icon={<Flame className="text-gold" size={18} />} />
        <StatCard eyebrow="Longest Streak" value={streak.longest} unit={streak.longest === 1 ? "day" : "days"} icon={<Sparkles className="text-gold" size={18} />} />
        <StatCard eyebrow="KAfrequency Points" value={automation?.rewards.balance ?? 0} unit="points" icon={<Gift className="text-gold" size={18} />} />
      </div>

      <div className="mt-10">
        <UserBadges />
      </div>

      {/* Membership + rewards */}
      <section className="mt-16 grid gap-4 lg:grid-cols-3">
        <DashboardPanel title="Membership" icon={<BadgeCheck size={14} aria-hidden />}>
          {automation?.subscription ? (
            <div>
              <p className="font-serif text-2xl text-gold-luminous">{automation.subscription.membership_tier ?? "Member"}</p>
              <p className="mt-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                {automation.subscription.status}{automation.subscription.environment === "sandbox" && " · test"}
              </p>
              {automation.subscription.current_period_end && (
                <p className="mt-4 text-sm text-muted-foreground">
                  {automation.subscription.cancel_at_period_end ? "Access through" : "Renews"} {new Date(automation.subscription.current_period_end).toLocaleDateString()}
                </p>
              )}
              {automation.subscription.price_id !== "lifetime_ascension_pass" && <ManageBillingButton />}
            </div>
          ) : (
            <EmptyInline>No active membership yet.</EmptyInline>
          )}
        </DashboardPanel>
        <DashboardPanel title="Referral" icon={<Repeat2 size={14} aria-hidden />}>
          {automation?.referral ? (
            <div>
              <p className="font-serif text-2xl text-gold-luminous">{automation.referral.code}</p>
              <p className="mt-2 break-all text-xs text-muted-foreground">
                {origin ? `${origin}/shop?ref=${automation.referral.code}` : automation.referral.code}
              </p>
            </div>
          ) : (
            <EmptyInline>Your referral link appears after your first order.</EmptyInline>
          )}
        </DashboardPanel>
        <DashboardPanel title="Rewards" icon={<Gift size={14} aria-hidden />}>
          {(automation?.rewards.recent.length ?? 0) > 0 ? (
            <ul className="space-y-3">
              {automation!.rewards.recent.slice(0, 3).map((reward) => (
                <li key={reward.id} className="flex items-start justify-between gap-3 text-sm">
                  <span className="text-muted-foreground">{reward.description ?? reward.reason}</span>
                  <span className="text-gold-luminous">+{reward.points}</span>
                </li>
              ))}
            </ul>
          ) : (
            <EmptyInline>Earn points with every purchase.</EmptyInline>
          )}
        </DashboardPanel>
      </section>

      {/* Library */}
      <section className="mt-16">
        <SectionTitle icon={<Library size={14} aria-hidden />} title="Library" count={automation?.library.length ?? 0} />
        {(automation?.library.length ?? 0) === 0 ? (
          <EmptyState hint="Digital purchases appear here permanently with secure re-download access." />
        ) : (
          <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {automation!.library.map((entry) => (
              <ProductMiniCard key={entry.id} product={entry.product} slug={entry.product_slug} footer={`Unlocked ${relativeTime(entry.granted_at)}`} />
            ))}
          </ul>
        )}
      </section>

      {/* Saved */}
      <section className="mt-16">
        <SectionTitle icon={<Heart size={14} aria-hidden />} title="Saved" count={savedItems.length} />
        {savedItems.length === 0 ? (
          <EmptyState hint="Tap “Save” on any release, teaching, activation, or book to keep it in your field." />
        ) : (
          <ul className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {savedItems.map(({ row, info }) => (
              <FavoriteRow key={row.id} row={row} info={info} />
            ))}
          </ul>
        )}
        {beatSaveCount > 0 && (
          <p className="mt-4 text-xs text-muted-foreground">
            You also have {beatSaveCount} saved {beatSaveCount === 1 ? "beat" : "beats"}.{" "}
            <Link to="/beats/account" className="text-gold hover:text-gold-luminous">
              View your beat wishlist
            </Link>
            .
          </p>
        )}
      </section>


      {/* Listening */}
      <section className="mt-16">
        <SectionTitle icon={<Music2 size={14} aria-hidden />} title="Recently played" count={listening.length} />
        {listening.length === 0 ? (
          <EmptyState hint="Play a preview or a track to build your listening trail." />
        ) : (
          <ul className="mt-6 divide-y hairline border-y hairline">
            {listening.map((l) => (
              <ListeningRow key={l.id} row={l} />
            ))}
          </ul>
        )}
      </section>

      {/* Recent completions */}
      <section className="mt-16">
        <SectionTitle icon={<Sparkles size={14} aria-hidden />} title="Recent activations" count={Math.min(completions.length, 20)} />
        {completions.length === 0 ? (
          <EmptyState hint="Mark an activation complete on any activation page to begin the streak." />
        ) : (
          <ul className="mt-6 divide-y hairline border-y hairline">
            {completions.slice(0, 20).map((c) => (
              <CompletionRow key={c.id} row={c} />
            ))}
          </ul>
        )}
      </section>

      {/* Orders */}
      <section className="mt-16">
        <SectionTitle icon={<ShoppingBag size={14} aria-hidden />} title="Orders" count={orders.length} />
        {orders.length === 0 ? (
          <EmptyState hint="Your orders from the Shop will appear here." />
        ) : (
          <ul className="mt-6 divide-y hairline border-y hairline">
            {orders.map((o) => (
              <li key={o.id} className="grid grid-cols-1 gap-2 py-4 sm:grid-cols-[1fr_auto] sm:items-start sm:gap-4">
                <div className="min-w-0">
                  {o.order_number && <p className="mb-1 text-[10px] uppercase tracking-[0.25em] text-gold">{o.order_number}</p>}
                  <Link to="/shop/$slug" params={{ slug: o.product_slug }} className="block truncate font-serif text-base hover:text-gold-luminous">
                    {o.product_title}
                  </Link>
                  <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] uppercase tracking-[0.2em] text-muted-foreground">
                    <span>{o.status}{o.environment === "sandbox" && " · test"}</span>
                    {o.shipping_address ? (
                      <span>· {o.fulfillment_status ?? "unfulfilled"}</span>
                    ) : null}
                    {o.tracking_number && (
                      <span>· tracking {o.tracking_url ? <a href={o.tracking_url} target="_blank" rel="noopener noreferrer" className="underline">{o.tracking_number}</a> : o.tracking_number}</span>
                    )}
                    {!!o.reward_points_awarded && <span>· +{o.reward_points_awarded} points</span>}
                  </div>
                </div>
                <div className="flex items-center gap-3 sm:justify-end">
                  <span className="text-sm text-gold-luminous">
                    {new Intl.NumberFormat("en-US", { style: "currency", currency: o.currency.toUpperCase() }).format(o.amount_cents / 100)}
                  </span>
                  {o.status === "paid" && !o.shipping_address && !o.refunded_at && (
                    <OrderDownloadButton orderId={o.id} />
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Recommendations + reviews */}
      <section className="mt-16 grid gap-8 lg:grid-cols-2">
        <div>
          <SectionTitle icon={<Sparkles size={14} aria-hidden />} title="Recommended" count={automation?.recommendations.length ?? 0} />
          {(automation?.recommendations.length ?? 0) === 0 ? (
            <EmptyState hint="Personal recommendations appear after purchases." />
          ) : (
            <ul className="mt-6 space-y-3">
              {automation!.recommendations.map((item) => (
                <ProductMiniCard key={item.id} product={item.product} slug={item.product_slug} footer={item.reason} />
              ))}
            </ul>
          )}
        </div>
        <div>
          <SectionTitle icon={<BadgeCheck size={14} aria-hidden />} title="Reviews" count={automation?.reviewRequests.length ?? 0} />
          {(automation?.reviewRequests.length ?? 0) === 0 ? (
            <EmptyState hint="Verified review requests are scheduled seven days after purchase." />
          ) : (
            <ul className="mt-6 space-y-3">
              {automation!.reviewRequests.map((request) => (
                <ProductMiniCard key={request.id} product={request.product} slug={request.product_slug} footer={`Review due ${new Date(request.due_at).toLocaleDateString()}`} />
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}

function OrderDownloadButton({ orderId }: { orderId: string }) {
  const [busy, setBusy] = useState(false);
  const [files, setFiles] = useState<Array<{ label: string; kind: string; format: string | null; sizeBytes: number | null; url: string }> | null>(null);
  const [err, setErr] = useState<string | null>(null);

  async function load() {
    setBusy(true); setErr(null);
    const res = await listMyDownloads({ data: { orderId } });
    setBusy(false);
    if ("error" in res) { setErr(res.error); return; }
    if (res.files.length === 1) {
      window.open(res.files[0].url, "_blank", "noopener");
      return;
    }
    setFiles(res.files);
  }

  if (files && files.length > 1) {
    return (
      <div className="flex flex-col items-end gap-1">
        <div className="max-h-56 overflow-auto rounded-md border hairline bg-background/60 p-2 text-xs">
          <p className="mb-1 text-[10px] uppercase tracking-[0.2em] text-muted-foreground">{files.length} files</p>
          <ul className="space-y-1">
            {files.map((f, i) => (
              <li key={i}>
                <a href={f.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 hover:text-gold-luminous">
                  <Download size={11} aria-hidden />
                  <span className="truncate max-w-[220px]">{f.label}</span>
                  {f.format && <span className="text-[10px] uppercase tracking-widest text-muted-foreground">· {f.format}</span>}
                </a>
              </li>
            ))}
          </ul>
        </div>
        <button type="button" onClick={() => setFiles(null)} className="text-[10px] uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground">Hide</button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        type="button"
        disabled={busy}
        onClick={load}
        className="inline-flex items-center gap-1.5 rounded-full border hairline px-3 py-1.5 text-xs hover:border-gold hover:text-gold disabled:opacity-60"
      >
        <Download size={12} aria-hidden /> {busy ? "Preparing…" : "Download"}
      </button>
      {err && <span className="text-[10px] text-red-400">{err}</span>}
    </div>
  );
}

// Kept for other callers (e.g. checkout return page) that expect the single-URL shape.
export async function _fetchSingleDownload(orderId: string) {
  return getMyDownload({ data: { orderId } });
}

function ManageBillingButton() {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  return (
    <div className="mt-5">
      <button
        type="button"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          setErr(null);
          const result = await createBillingPortalSession({
            data: { returnUrl: window.location.href, environment: getStripeEnvironment() },
          });
          setBusy(false);
          if ("error" in result) { setErr(result.error); return; }
          window.open(result.url, "_blank", "noopener");
        }}
        className="rounded-full border hairline px-4 py-2 text-xs hover:border-gold hover:text-gold disabled:opacity-60"
      >
        {busy ? "Opening…" : "Manage billing"}
      </button>
      {err && <p className="mt-2 text-xs text-red-400">{err}</p>}
    </div>
  );
}


function StatCard({ eyebrow, value, unit, icon }: { eyebrow: string; value: number; unit: string; icon: React.ReactNode }) {
  return (
    <div className="rounded-xl border hairline bg-charcoal p-6">
      <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold">{icon} {eyebrow}</div>
      <p className="mt-3 font-serif text-4xl text-gold-luminous">{value}<span className="ml-2 text-sm text-muted-foreground">{unit}</span></p>
    </div>
  );
}

function SectionTitle({ icon, title, count }: { icon: React.ReactNode; title: string; count: number }) {
  return (
    <div className="flex items-baseline justify-between border-b hairline pb-3">
      <h2 className="inline-flex items-center gap-2 font-serif text-2xl">{icon} {title}</h2>
      <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">{count}</span>
    </div>
  );
}

function EmptyState({ hint }: { hint: string }) {
  return (
    <p className="mt-6 rounded-xl border hairline bg-charcoal p-8 text-sm text-muted-foreground">{hint}</p>
  );
}

function EmptyInline({ children }: { children: React.ReactNode }) {
  return <p className="text-sm text-muted-foreground">{children}</p>;
}

function DashboardPanel({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border hairline bg-charcoal p-6">
      <h2 className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-gold">{icon} {title}</h2>
      <div className="mt-5">{children}</div>
    </div>
  );
}

function ProductMiniCard({ product, slug, footer }: { product: ShopProduct | null; slug: string; footer: string }) {
  return (
    <li className="rounded-lg border hairline bg-charcoal p-4">
      <p className="text-[10px] uppercase tracking-[0.25em] text-gold">{product?.kind ?? "Product"}</p>
      <Link to="/shop/$slug" params={{ slug }} className="mt-2 block truncate font-serif text-lg hover:text-gold-luminous">
        {product?.title ?? slug}
      </Link>
      {product?.subtitle && <p className="mt-1 truncate text-xs text-muted-foreground">{product.subtitle}</p>}
      <p className="mt-3 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">{footer}</p>
    </li>
  );
}

function FavoriteRow({ row, info }: { row: FavRow; info: { title: string; href: string } }) {
  return (
    <li className="rounded-lg border hairline bg-charcoal p-4">
      <p className="text-[10px] uppercase tracking-[0.25em] text-gold">{labelForKind(row.kind)}</p>
      <Link to={info.href} className="mt-2 block truncate font-serif text-lg hover:text-gold-luminous">
        {info.title}
      </Link>
      <Link to={info.href} className="mt-3 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-gold">
        Open <ArrowRight size={10} />
      </Link>
    </li>
  );
}

function ListeningRow({ row }: { row: ListenRow }) {
  const release = RELEASES.find((r) => r.slug === row.release_slug);
  if (!release) return null;
  const song = row.song_slug ? release.tracks?.find((t) => t.slug === row.song_slug) : null;
  const href = song?.slug ? `/music/${release.slug}/song/${song.slug}` : `/music/${release.slug}`;
  return (
    <li className="grid grid-cols-[1fr_auto] items-center gap-4 py-3">
      <div className="min-w-0">
        <Link to={href} className="block truncate font-serif text-base hover:text-gold-luminous">
          {song ? song.title : release.title}
        </Link>
        <p className="text-xs text-muted-foreground">{song ? release.title : release.type}</p>
      </div>
      <time className="shrink-0 text-[11px] uppercase tracking-[0.2em] text-muted-foreground" dateTime={row.played_at}>
        {relativeTime(row.played_at)}
      </time>
    </li>
  );
}

function CompletionRow({ row }: { row: CompletionRow }) {
  const activation = ACTIVATIONS.find((a) => a.slug === row.activation_slug);
  if (!activation) return null;
  return (
    <li className="grid grid-cols-[1fr_auto] items-center gap-4 py-3">
      <Link to="/activations/$slug" params={{ slug: activation.slug }} className="min-w-0 truncate font-serif text-base hover:text-gold-luminous">
        {activation.title}
      </Link>
      <time className="shrink-0 text-[11px] uppercase tracking-[0.2em] text-muted-foreground" dateTime={row.completed_at}>
        {relativeTime(row.completed_at)}
      </time>
    </li>
  );
}

function labelForKind(k: FavKind) {
  return { music: "Release", song: "Song", wisdom: "Teaching", activation: "Activation", book: "Book", beat: "Beat", beat_wishlist: "Beat" }[k];
}

function resolveItem(kind: FavKind, slug: string): { title: string; href: string } | null {
  if (kind === "music") {
    const r = RELEASES.find((x) => x.slug === slug);
    return r ? { title: r.title, href: `/music/${r.slug}` } : null;
  }
  if (kind === "song") {
    for (const r of RELEASES) {
      const t = r.tracks?.find((x) => x.slug === slug);
      if (t) return { title: `${t.title} · ${r.title}`, href: `/music/${r.slug}/song/${t.slug}` };
    }
    return null;
  }
  if (kind === "wisdom") {
    const w = WISDOM.find((x) => x.slug === slug);
    return w ? { title: w.title, href: `/wisdom/${w.slug}` } : null;
  }
  if (kind === "activation") {
    const a = ACTIVATIONS.find((x) => x.slug === slug);
    return a ? { title: a.title, href: `/activations/${a.slug}` } : null;
  }
  if (kind === "book") {
    const b = BOOKS.find((x) => x.slug === slug);
    return b ? { title: b.title, href: `/books/${b.slug}` } : null;
  }
  return null;
}

function relativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  const now = Date.now();
  const diff = Math.max(0, now - then);
  const min = Math.floor(diff / 60_000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const days = Math.floor(hr / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  return `${months}mo ago`;
}

function calculateStreak(dates: string[]): { current: number; longest: number } {
  if (!dates.length) return { current: 0, longest: 0 };
  // Bucket to local YYYY-MM-DD
  const days = new Set<string>();
  for (const iso of dates) {
    const d = new Date(iso);
    days.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
  }
  const sorted = Array.from(days)
    .map((k) => {
      const [y, m, d] = k.split("-").map(Number);
      return new Date(y, m, d).getTime();
    })
    .sort((a, b) => b - a);
  const oneDay = 86_400_000;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = today.getTime() - oneDay;
  let current = 0;
  if (sorted[0] === today.getTime() || sorted[0] === yesterday) {
    current = 1;
    for (let i = 1; i < sorted.length; i++) {
      if (sorted[i - 1] - sorted[i] === oneDay) current += 1;
      else break;
    }
  }
  let longest = 1;
  let run = 1;
  for (let i = 1; i < sorted.length; i++) {
    if (sorted[i - 1] - sorted[i] === oneDay) {
      run += 1;
      longest = Math.max(longest, run);
    } else {
      run = 1;
    }
  }
  return { current, longest };
}
